"use client";

import React, { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Edit2, Save, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import type { Communications } from "@/src/gql/graphql";

interface CommunicationsInlineEditPanelProps {
  item: Communications;
  onUpdate: (updates: Partial<Communications>) => Promise<void>;
  isLoading?: boolean;
}

interface EditableFields {
  title: string;
  body: string;
  status: string;
}

function toEditValues(item: Communications): EditableFields {
  return {
    title: item.title ?? "",
    body: item.body ?? "",
    status: item.status ?? "",
  };
}

/**
 * Communications Inline Edit Panel Component
 * Allows quick inline editing of communication fields
 * Provides a compact editing interface for common fields
 */
export function CommunicationsInlineEditPanel({
  item,
  onUpdate,
  isLoading = false,
}: CommunicationsInlineEditPanelProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditableFields>(() => toEditValues(item));
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValues(toEditValues(item));
  }, [item]);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      const updates: Partial<Communications> = {};

      if (editValues.title !== (item.title ?? "")) {
        updates.title = editValues.title;
      }
      if (editValues.body !== (item.body ?? "")) {
        updates.body = editValues.body;
      }
      if (editValues.status !== (item.status ?? "")) {
        updates.status = editValues.status;
      }

      if (Object.keys(updates).length === 0) {
        toast.info("No changes to save");
        setIsEditing(false);
        return;
      }

      await onUpdate(updates);
      setIsEditing(false);
      toast.success("Communication updated successfully");
    } catch (error) {
      console.error("Failed to update communication:", error);
      toast.error("Failed to update communication");
    } finally {
      setIsSaving(false);
    }
  }, [editValues, item, onUpdate]);

  if (!isEditing) {
    return (
      <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Quick Edit</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEdit}
              disabled={isLoading}
              className="gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Title:</span>
            <p className="text-gray-600 dark:text-gray-400 truncate">{item.title || "No title"}</p>
          </div>
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
            <div className="mt-1">
              <Badge variant="outline">{item.status || "Unknown"}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <CardTitle className="text-sm font-semibold">Editing Mode</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Title Field */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
          <Input
            value={editValues.title}
            onChange={(e) => setEditValues((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Communication title"
            disabled={isSaving}
            className="text-sm"
          />
        </div>

        {/* Body Field */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
          <Textarea
            value={editValues.body}
            onChange={(e) => setEditValues((prev) => ({ ...prev, body: e.target.value }))}
            placeholder="Communication details"
            disabled={isSaving}
            className="text-sm min-h-[100px]"
          />
        </div>

        {/* Status Field */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
          <select
            value={editValues.status}
            onChange={(e) => setEditValues((prev) => ({ ...prev, status: e.target.value }))}
            disabled={isSaving}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            size="sm"
            className="gap-2 flex-1"
          >
            <Save className="w-4 h-4" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button
            onClick={handleCancel}
            disabled={isSaving}
            variant="outline"
            size="sm"
            className="gap-2 flex-1"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
